import api from "./api"; // Your axios instance

// ============================================================
// Types
// ============================================================

export interface ProgressResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
}

export interface LearningContentCompleteData {
  moduleId: string;
  weekNumber: number;
  completedAt: string;
  alreadyCompleted?: boolean;
}

export interface QuizSubmitData {
  moduleId: string;
  weekNumber: number;
  score: number;
  isComplete: boolean;
  attempts: number;
}

// ============================================================
// Learning Progress Service
// ============================================================

export const learningProgressService = {
  /**
   * Mark learning content as completed
   * Call when student finishes viewing the learning content
   */
  async markLearningContentCompleted(
    weekNumber: number,
    moduleId: string
  ): Promise<ProgressResponse<LearningContentCompleteData>> {
    try {
      const response = await api.post("/students/learning-content/complete", {
        weekNumber,
        moduleId,
      });
      return {
        success: true,
        message: response.data.message,
        data: response.data.data,
      };
    } catch (error: any) {
      console.error("Error marking learning content completed:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to update progress",
      };
    }
  },

  /**
   * Submit quiz progress
   * Call when student submits a quiz
   */
  async submitQuizProgress(
    weekNumber: number,
    moduleId: string,
    score: number,
    isComplete: boolean
  ): Promise<ProgressResponse<QuizSubmitData>> {
    try {
      const response = await api.post("/students/quiz/submit", {
        weekNumber,
        moduleId,
        score,
        isComplete,
      });
      return {
        success: true,
        message: response.data.message,
        data: response.data.data,
      };
    } catch (error: any) {
      console.error("Error submitting quiz progress:", error);
      return {
        success: false,
        message:
          error.response?.data?.message || "Failed to save quiz progress",
      };
    }
  },

  /**
   * Reset quiz for retake
   */
  async resetQuizProgress(
    weekNumber: number,
    moduleId: string
  ): Promise<ProgressResponse> {
    try {
      const response = await api.post("/students/quiz/reset", {
        weekNumber,
        moduleId,
      });
      return {
        success: true,
        message: response.data.message,
        data: response.data.data,
      };
    } catch (error: any) {
      console.error("Error resetting quiz:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to reset quiz",
      };
    }
  },
};

export default learningProgressService;
